import { axiosAzureAPI } from './axiosService';

// Native, ownership-checked record files served by elgin-azure-api (/api/box/...).
// The server resolves the Box folder from a record the user owns; it never returns a Box token.

export interface RecordFile {
    id: string;
    name: string;
    size: number;
    createdAt: string | null;
    modifiedAt: string | null;
    /** True only for files uploaded through the portal; the server refuses to delete anything else. */
    deletable: boolean;
}

export interface RecordFilesResponse {
    files: RecordFile[];
}

export interface DownloadUrlResponse {
    url: string;
    expiresAt: string;
}

/**
 * Record types the server accepts for `:type`: Salesforce API names plus the legacy labels it maps.
 * Anything else (e.g. COE_Permit__c) is rejected with 400, so callers must not request it.
 */
export const RECORD_FILE_TYPES = [
    'Case',
    'Business_Application__c',
    'Zoning_Inquiry__c',
    'LPI_Application__c',
    'Business Application',
    'Zoning Inquiry',
    'Building Permit',
] as const;

export type RecordFileType = typeof RECORD_FILE_TYPES[number];

export const isRecordFileType = (value: unknown): value is RecordFileType =>
    typeof value === 'string' && (RECORD_FILE_TYPES as readonly string[]).includes(value);

const isNullableString = (value: unknown): value is string | null => value === null || typeof value === 'string';

const hasRecordFileFields = (v: Record<string, unknown>): boolean =>
    typeof v.id === 'string'
    && v.id !== ''
    && typeof v.name === 'string'
    && typeof v.size === 'number'
    && isNullableString(v.createdAt)
    && isNullableString(v.modifiedAt);

/** Strict runtime check for a `RecordFile`, including its `deletable` flag. */
export const isRecordFile = (value: unknown): value is RecordFile => {
    if (typeof value !== 'object' || value === null) return false;
    const v = value as Record<string, unknown>;
    return hasRecordFileFields(v) && typeof v.deletable === 'boolean';
};

/**
 * Validates a file returned by the server. A missing `deletable` (an API that predates the flag) is
 * read as false so Delete stays hidden; any other non-boolean value is rejected.
 */
export const parseRecordFile = (value: unknown): RecordFile | undefined => {
    if (typeof value !== 'object' || value === null) return undefined;
    const v = value as Record<string, unknown>;
    if (!hasRecordFileFields(v)) return undefined;
    if (v.deletable !== undefined && typeof v.deletable !== 'boolean') return undefined;
    return {
        id: v.id as string,
        name: v.name as string,
        size: v.size as number,
        createdAt: v.createdAt as string | null,
        modifiedAt: v.modifiedAt as string | null,
        deletable: v.deletable === true,
    };
};

/** Validates a `RecordFilesResponse` body; throws so callers land in their error state. */
export const parseRecordFilesResponse = (body: unknown): RecordFile[] => {
    const files = typeof body === 'object' && body !== null ? (body as Record<string, unknown>).files : undefined;
    if (!Array.isArray(files)) throw new Error('Unexpected file list response');
    return files.map(item => {
        const file = parseRecordFile(item);
        if (!file) throw new Error('Unexpected file in file list response');
        return file;
    });
};

const BOX_DOWNLOAD_HOST_SUFFIXES = ['.boxcloud.com', '.box.com'];

/**
 * True for an https Box download URL. Download URLs come from our API, but we navigate the whole
 * page to them, so anything unexpected (wrong scheme, host, a port or embedded credentials) is
 * refused rather than followed.
 */
export const isBoxDownloadUrl = (url: unknown): url is string => {
    if (typeof url !== 'string') return false;
    try {
        const { protocol, hostname, port, username, password } = new URL(url);
        const host = hostname.toLowerCase();
        return protocol === 'https:'
            && port === ''
            && username === ''
            && password === ''
            && BOX_DOWNLOAD_HOST_SUFFIXES.some(suffix => host.endsWith(suffix));
    } catch {
        return false;
    }
};

/** Client-side mirror of the server's 50 MB upload limit. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

const seg = encodeURIComponent;

const recordBase = (type: RecordFileType, sfId: string) => `/api/box/records/${seg(type)}/${seg(sfId)}`;
const utilityBase = (accountId: string) => `/api/box/utility/${seg(accountId)}`;

export const listRecordFiles = async (type: RecordFileType, sfId: string): Promise<RecordFile[]> => {
    const response = await axiosAzureAPI.get<unknown>(`${recordBase(type, sfId)}/items`);
    return parseRecordFilesResponse(response.data);
};

export const getRecordFileDownloadUrl = async (type: RecordFileType, sfId: string, fileId: string): Promise<DownloadUrlResponse> => {
    const response = await axiosAzureAPI.get<DownloadUrlResponse>(`${recordBase(type, sfId)}/files/${seg(fileId)}/download-url`);
    return response.data;
};

export const uploadRecordFile = async (type: RecordFileType, sfId: string, file: File): Promise<RecordFile> => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const response = await axiosAzureAPI.post<RecordFile>(`${recordBase(type, sfId)}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

export const deleteRecordFile = async (type: RecordFileType, sfId: string, fileId: string): Promise<void> => {
    await axiosAzureAPI.delete(`${recordBase(type, sfId)}/files/${seg(fileId)}`);
};

export const listUtilityFiles = async (accountId: string): Promise<RecordFile[]> => {
    const response = await axiosAzureAPI.get<unknown>(`${utilityBase(accountId)}/items`);
    return parseRecordFilesResponse(response.data);
};

export const getUtilityFileDownloadUrl = async (accountId: string, fileId: string): Promise<DownloadUrlResponse> => {
    const response = await axiosAzureAPI.get<DownloadUrlResponse>(`${utilityBase(accountId)}/files/${seg(fileId)}/download-url`);
    return response.data;
};

interface HttpErrorShape {
    response?: { status?: number; data?: { message?: unknown } };
}

/** HTTP status of an axios error, if any. */
export const getErrorStatus = (err: unknown): number | undefined =>
    (err as HttpErrorShape | undefined)?.response?.status;

/** Server-supplied `{ message }` (Nest default error body), if it is a usable string. */
export const getErrorMessage = (err: unknown): string | undefined => {
    const message = (err as HttpErrorShape | undefined)?.response?.data?.message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && message.every(m => typeof m === 'string') && message.length) return message.join(', ');
    return undefined;
};
